import { inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { environment } from '@env';
import { AuthService } from '../auth.service';
import { UserTypeService } from '../user-type.service';
import { NAVIGATION_ROUTES } from '@shared/constants';

/**
 * Guard para redirigir automáticamente según el dominio y sesión activa
 * 
 * Lógica de redirección:
 * 1. Si hay sesión activa:
 *    - Usuario Maestro → /inicio
 *    - Usuario Owner → /owner/inicio
 * 2. Si NO hay sesión:
 *    - Dominio construyo.pe → /owner (landing)
 *    - Dominio cantera.pe → / (landing maestro)
 * 
 * Mejores prácticas:
 * - Los dominios se configuran en los archivos environment.ts
 * - Usa DOCUMENT para ser compatible con SSR (Server-Side Rendering)
 * - Usa route.queryParamMap en lugar de parsear URLs manualmente (más rápido y confiable)
 */
export const domainRedirectGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state) => {
  const router = inject(Router);
  const document = inject(DOCUMENT);
  const authService = inject(AuthService);
  const userTypeService = inject(UserTypeService);
  
  // Obtener solo el path sin query params
  const urlPath = state.url.split('?')[0];
  
  // Solo actuar si estamos en la raíz
  if (urlPath !== NAVIGATION_ROUTES.ROOT) {
    return true;
  }
  
  // 1. Si hay sesión activa, redirigir según el tipo de usuario
  if (authService.isAuthenticated()) {
    const userType = userTypeService.getUserType();
    
    if (userType === 'maestro') {
      router.navigate([NAVIGATION_ROUTES.INICIO]);
      return false;
    }
    
    if (userType === 'owner') {
      router.navigate([NAVIGATION_ROUTES.OWNER_INICIO]);
      return false;
    }
  }
  
  // 2. Si NO hay sesión, redirigir según el dominio
  const hostname = document.location?.hostname || '';
  const testParam = route.queryParamMap.get('test');
  
  const isOwnerDomain = hostname.includes(environment.domains.owner);
  const isTestMode = testParam === 'owner';
  
  if (isOwnerDomain || isTestMode) {
    router.navigate([NAVIGATION_ROUTES.OWNER_ROOT]);
    return false;
  }
  
  // Si es dominio maestro (cantera.pe) sin sesión, mostrar landing maestro
  return true;
};
